import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SpeedIcon from '@mui/icons-material/Speed';
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import { Filter } from '@cubejs-client/core';
import { cubejsApi } from '../../../app.functions';
import CustomDateRangePicker from '../../../components/FormControl/CustomDateRangePicker';
import { useSpeedUnitPreference } from 'hooks/useSpeedUnitPreference';
import { Group } from 'utils';
import { viewGroup } from '../api/groups.api';

type ReportKey = 'speed-leaderboard';

interface ReportCard {
    key: ReportKey;
    label: string;
    description: string;
    icon: React.ReactNode;
}

const REPORT_CARDS: ReportCard[] = [
    {
        key: 'speed-leaderboard',
        label: 'Speed Leaderboard',
        description: 'Top max speeds per athlete',
        icon: <SpeedIcon />,
    },
];

interface LeaderboardRow {
    individualUuid: string;
    name: string;
    maxSpeedMph: number;
}

const defaultDateRange = (): [DateTime, DateTime] => {
    const end = DateTime.local();
    const start = end.minus({ months: 3 });
    return [start, end];
};

const getInitials = (name: string): string => {
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
};

const MEDAL_COLORS: Record<number, string> = {
    1: '#F5B400',
    2: '#A8B0BD',
    3: '#CD7F32',
};

const GroupReports: React.FC = () => {
    const theme = useTheme();
    const { id } = useParams<{ id: string }>();
    const { push, goBack } = useHistory();
    const { convert, label: speedLabel } = useSpeedUnitPreference();

    const [query, setQuery] = useQueryParams({ report: StringParam });
    const activeReport: ReportKey =
        query.report === 'speed-leaderboard' ? 'speed-leaderboard' : 'speed-leaderboard';

    const [group, setGroup] = useState<Group>();
    const [dateRange, setDateRange] = useState<[DateTime | null, DateTime | null]>(
        defaultDateRange(),
    );
    const [rows, setRows] = useState<LeaderboardRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!query.report) {
            setQuery({ report: 'speed-leaderboard' }, 'replaceIn');
        }
    }, [query.report, setQuery]);

    useEffect(() => {
        let cancelled = false;
        viewGroup(id)
            .then((res) => {
                if (!cancelled) setGroup(res.data);
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load group');
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    const loadSpeedLeaderboard = useCallback(() => {
        const [start, end] = dateRange;
        const filters: Filter[] = [
            {
                member: 'DimMember.groupUuid',
                operator: 'equals',
                values: [id],
            },
        ];

        setLoading(true);
        setError('');
        cubejsApi
            .load({
                dimensions: ['DimIndividual.uuid', 'DimIndividual.name'],
                measures: ['FactSprintsRanked.vMaxMaxMph'],
                filters,
                timeDimensions:
                    start && end
                        ? [
                              {
                                  dimension: 'DimDate.fullDate',
                                  dateRange: [
                                      start.toISODate() as string,
                                      end.toISODate() as string,
                                  ],
                              },
                          ]
                        : undefined,
                order: {
                    'FactSprintsRanked.vMaxMaxMph': 'desc',
                },
                limit: 1000,
            })
            .then((resultSet) => {
                const data = resultSet.tablePivot();
                const mapped: LeaderboardRow[] = data
                    .map((d) => ({
                        individualUuid: d['DimIndividual.uuid'] as string,
                        name: (d['DimIndividual.name'] as string) ?? 'Unknown',
                        maxSpeedMph: Number(d['FactSprintsRanked.vMaxMaxMph'] ?? 0),
                    }))
                    .filter((r) => r.maxSpeedMph > 0);
                setRows(mapped);
            })
            .catch(() => setError('Failed to load report data'))
            .finally(() => setLoading(false));
    }, [id, dateRange]);

    useEffect(() => {
        if (activeReport === 'speed-leaderboard') {
            loadSpeedLeaderboard();
        }
    }, [activeReport, loadSpeedLeaderboard]);

    const csvFilename = useMemo(() => {
        const [start, end] = dateRange;
        const groupSlug = (group?.name ?? 'group').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        const range = start && end ? `${start.toISODate()}_to_${end.toISODate()}` : 'all-time';
        return `${groupSlug}-speed-leaderboard-${range}.csv`;
    }, [group, dateRange]);

    const handleExportCsv = useCallback(() => {
        const header = ['Rank', 'Athlete', `Max Speed (${speedLabel})`];
        const lines = [header.join(',')];
        rows.forEach((row, idx) => {
            const speed = convert(row.maxSpeedMph).toFixed(2);
            const safeName = `"${row.name.replace(/"/g, '""')}"`;
            lines.push([idx + 1, safeName, speed].join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', csvFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [rows, convert, speedLabel, csvFilename]);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <IconButton
                    onClick={() => (group ? push(`/community/groups/${id}`) : goBack())}
                    size="small"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                        Reports
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {group?.name ?? 'Group'}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
                {REPORT_CARDS.map((card) => {
                    const selected = activeReport === card.key;
                    return (
                        <Paper
                            key={card.key}
                            elevation={0}
                            onClick={() => setQuery({ report: card.key }, 'replaceIn')}
                            sx={{
                                px: 2.5,
                                py: 2,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: selected ? 'primary.main' : 'divider',
                                bgcolor: selected
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : 'background.paper',
                                minWidth: 240,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 2,
                                },
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1.5,
                                        bgcolor: selected
                                            ? 'primary.main'
                                            : alpha(theme.palette.primary.main, 0.1),
                                        color: selected ? 'primary.contrastText' : 'primary.main',
                                    }}
                                >
                                    {card.icon}
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {card.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {card.description}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            {activeReport === 'speed-leaderboard' && (
                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                        sx={{ mb: 3 }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <EmojiEventsIcon sx={{ color: MEDAL_COLORS[1], fontSize: 32 }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Speed Leaderboard
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Max speed per athlete, highest first
                                    {rows.length > 0 && ` • ${rows.length} athletes`}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            <CustomDateRangePicker
                                value={dateRange}
                                onChange={(value) => setDateRange(value)}
                            />
                            <Button
                                variant="contained"
                                disableElevation
                                startIcon={<DownloadIcon />}
                                onClick={handleExportCsv}
                                disabled={loading || rows.length === 0}
                            >
                                Export CSV
                            </Button>
                        </Stack>
                    </Stack>

                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <SpeedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                            <Typography color="text.secondary">
                                No sprint data found for the selected date range.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                width: 80,
                                            }}
                                        >
                                            Rank
                                        </TableCell>
                                        <TableCell
                                            sx={{ fontWeight: 600, color: 'text.secondary' }}
                                        >
                                            Athlete
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 600, color: 'text.secondary' }}
                                        >
                                            Max Speed ({speedLabel})
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => {
                                        const rank = idx + 1;
                                        const medalColor = MEDAL_COLORS[rank];
                                        return (
                                            <TableRow
                                                key={row.individualUuid}
                                                hover
                                                sx={{
                                                    '& td': { py: 1.5 },
                                                    '&:last-child td': { borderBottom: 0 },
                                                }}
                                            >
                                                <TableCell>
                                                    {medalColor ? (
                                                        <Chip
                                                            label={`#${rank}`}
                                                            size="small"
                                                            icon={
                                                                <EmojiEventsIcon
                                                                    sx={{
                                                                        color: `${medalColor} !important`,
                                                                    }}
                                                                />
                                                            }
                                                            sx={{
                                                                bgcolor: alpha(medalColor, 0.15),
                                                                color: 'text.primary',
                                                                fontWeight: 600,
                                                                border: '1px solid',
                                                                borderColor: alpha(medalColor, 0.4),
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ pl: 1.5, fontWeight: 500 }}
                                                        >
                                                            #{rank}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1.5}
                                                        alignItems="center"
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                fontSize: 13,
                                                                bgcolor: alpha(
                                                                    theme.palette.primary.main,
                                                                    0.12,
                                                                ),
                                                                color: 'primary.main',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {getInitials(row.name)}
                                                        </Avatar>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {row.name}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: rank <= 3 ? 700 : 500,
                                                            fontVariantNumeric: 'tabular-nums',
                                                            color:
                                                                rank === 1
                                                                    ? 'primary.main'
                                                                    : 'text.primary',
                                                        }}
                                                    >
                                                        {convert(row.maxSpeedMph).toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}
        </Container>
    );
};

export default GroupReports;
